import { useSuspenseQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toRegularMetricResultOrError } from '../../../api/utils';
import { UNDEFINED_DISPLAY_KEY } from '../../../constants';
import { useMetricDetailsMapContext, useMetricServiceContext } from '../../../context/contexts';
import { TimeSelection } from '../../../types';
import { formatMetricValue } from '../../../utils-display';
import { MetricTypes } from '../../types';
import { getFilterConditions } from '../../utils';
import { toNonSegmentedTimePeriodChartInput } from '../charts/mui-charts/utils';
import {
  timeSliderStateToTimePeriodTimeSelection,
  toPartialNonSegmentedChartDataTimePeriod,
} from '../charts/timeperiod/utils';
import { KpiSectionValue, KpiSectionValueInput } from './types';

export const useSimplifiedKpiSectionValues = (kpiSectionValues: KpiSectionValueInput[]): KpiSectionValue[] => {
  const { t } = useTranslation();
  const metricDetailsMap = useMetricDetailsMapContext();
  const metricService = useMetricServiceContext();
  // validating queries
  if (kpiSectionValues.some(({ query }) => query.segmentations && query.segmentations.length > 0)) {
    throw new Error('SimplifiedKpiSectionValue does not support segmentation');
  }
  const queries = kpiSectionValues.map(({ query, manualDataRefetchCountHandle, timeSliderState, filtersState }) => {
    const { metrics, timeSelection, filters, segmentations } = query;
    const finalTimeSelection: TimeSelection = timeSliderState
      ? timeSliderStateToTimePeriodTimeSelection(timeSliderState)
      : timeSelection;
    const allFilters =
      [getFilterConditions(filtersState.items, MetricTypes.REGULAR, false), filters]
        .flatMap((f) => (f ? [f] : []))
        .join(' AND ') || undefined;
    const queryKey = JSON.stringify([
      'kpi',
      metrics,
      finalTimeSelection,
      allFilters,
      segmentations,
      manualDataRefetchCountHandle.current,
    ]);
    return {
      queryKey: [queryKey],
      queryFn: () =>
        toRegularMetricResultOrError(
          metricService.queryRegularMetrics,
          metrics,
          finalTimeSelection,
          allFilters,
          segmentations
        ),
    };
  });
  const queryResults = useSuspenseQueries({
    queries,
  });
  const allData = queryResults.map(({ data }) => data);
  const formattedValues = allData.map((data) => {
    const chartInput = toNonSegmentedTimePeriodChartInput(data ?? [], toPartialNonSegmentedChartDataTimePeriod);
    const series = chartInput.series;
    if (!series) {
      return {
        value: null,
        valueFormatter: () => {
          // noop
          throw new Error('This should never be called if value is null');
        },
      };
    }
    const metricDetails = metricDetailsMap[series.metricId.value];
    const valueFormatter = (value: number) =>
      formatMetricValue(value, metricDetails.formatType) ?? t(UNDEFINED_DISPLAY_KEY);
    return {
      value: series.values[0] as number,
      valueFormatter,
    };
  });
  return formattedValues;
};
